// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { Component, createContext, useContext, useState } from 'react';
import { useMergeRefs } from '@cloudscape-design/component-toolkit/internal';
import { metrics } from '../internal/metrics';
import { ErrorBoundaryFallback } from './fallback';
import styles from './styles.css.js';
const RootSuppressed = Symbol();
const ErrorBoundariesContext = createContext({
    onError: () => { },
    suppressed: RootSuppressed,
});
export function InternalErrorBoundary({ children, suppressNested, suppressible, __internalRootRef, ...props }) {
    const context = useContext(ErrorBoundariesContext);
    const thisSuppressed = context.suppressed === true && suppressible;
    const nextSuppressed = suppressNested || thisSuppressed;
    const [forcedError, setForcedError] = useState(false);
    const setElement = (node) => {
        if (node) {
            if (!node.__awsui__) {
                node.__awsui__ = {};
            }
            node.__awsui__.forceError = () => setForcedError(true);
            node.__awsui__.clearForcedError = () => setForcedError(false);
        }
    };
    const mergedRef = useMergeRefs(setElement, __internalRootRef);
    return (React.createElement("div", { ref: mergedRef, className: styles['error-boundary'], "data-awsui-boundary-id": props.errorBoundaryId }, !thisSuppressed ? (React.createElement(ErrorBoundaryImpl, { ...props, forcedError: forcedError },
        React.createElement(ErrorBoundariesContext.Provider, { value: { ...props, suppressed: nextSuppressed } }, children))) : (React.createElement(React.Fragment, null, children))));
}
export function BuiltInErrorBoundary({ wrapper, suppressNested = false, children }) {
    const context = useContext(ErrorBoundariesContext);
    const thisSuppressed = context.suppressed === true || context.suppressed === RootSuppressed;
    const nextSuppressed = suppressNested || thisSuppressed;
    return !thisSuppressed ? (React.createElement(ErrorBoundaryImpl, { ...context, wrapper: wrapper },
        React.createElement(ErrorBoundariesContext.Provider, { value: { ...context, suppressed: nextSuppressed } }, children))) : (React.createElement(React.Fragment, null, children));
}
export function AppLayoutBuiltInErrorBoundary({ wrapper, suppressNested = false, children, renderFallback = () => React.createElement(React.Fragment, null), appLayoutPart, }) {
    const context = useContext(ErrorBoundariesContext);
    const thisSuppressed = context.suppressed === true || context.suppressed === RootSuppressed;
    const nextSuppressed = suppressNested || thisSuppressed;
    return (React.createElement(ErrorBoundaryImpl, { ...context, wrapper: wrapper, renderFallback: renderFallback, className: styles['app-layout-part-fallback'], onError: error => {
            var _a, _b, _c;
            (_a = context === null || context === void 0 ? void 0 : context.onError) === null || _a === void 0 ? void 0 : _a.call(context, error);
            metrics.sendOpsMetricObject('awsui-app-layout-error-boundary-fired', {
                errorMessage: (_c = (_b = error === null || error === void 0 ? void 0 : error.error) === null || _b === void 0 ? void 0 : _b.message) !== null && _c !== void 0 ? _c : '',
                appLayoutPart: appLayoutPart !== null && appLayoutPart !== void 0 ? appLayoutPart : '',
            });
            console.log(`[AwsUiAppLayoutError] appLayoutPart=${appLayoutPart !== null && appLayoutPart !== void 0 ? appLayoutPart : ''}`, error === null || error === void 0 ? void 0 : error.error);
        } },
        React.createElement(ErrorBoundariesContext.Provider, { value: { ...context, suppressed: nextSuppressed, renderFallback } }, children)));
}
class ErrorBoundaryImpl extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    componentDidCatch(error, errorInfo) {
        var _a, _b;
        (_b = (_a = this.props).onError) === null || _b === void 0 ? void 0 : _b.call(_a, { error, errorInfo, errorBoundaryId: this.props.errorBoundaryId });
    }
    render() {
        var _a, _b, _c;
        if (this.state.hasError || this.props.forcedError) {
            const fallback = React.createElement(ErrorBoundaryFallback, { ...this.props });
            return (_c = (_b = (_a = this.props).wrapper) === null || _b === void 0 ? void 0 : _b.call(_a, fallback)) !== null && _c !== void 0 ? _c : fallback;
        }
        return this.props.children;
    }
}
//# sourceMappingURL=internal.js.map